import { useCallback, useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Upload } from "lucide-react";

const empty = { title: "", subtitle: "", before_id: "", after_id: "", order: 0, active: true };

export default function AdminBeforeAfter() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [uploading, setUploading] = useState(null);

    const load = useCallback(() => api.get("/admin/before-after").then((r) => setItems(r.data)), []);
    useEffect(() => { load(); }, [load]);

    const upload = async (file, field) => {
        setUploading(field);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd);
            setEditing((s) => ({ ...s, [field]: data.id }));
        } catch { toast.error("Falha no upload"); }
        finally { setUploading(null); }
    };

    const save = async () => {
        if (!editing.before_id || !editing.after_id) return toast.error("Selecione as duas fotografias");
        if (!editing.title) return toast.error("Título obrigatório");
        try {
            if (editing.id) await api.put(`/admin/before-after/${editing.id}`, editing);
            else await api.post("/admin/before-after", editing);
            toast.success("Guardado");
            setEditing(null);
            load();
        } catch { toast.error("Erro ao guardar"); }
    };

    const del = async (id) => {
        if (!window.confirm("Eliminar este antes/depois?")) return;
        await api.delete(`/admin/before-after/${id}`);
        toast.success("Eliminado");
        load();
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <h1 className="font-display text-3xl font-bold text-stone-900">Antes & Depois</h1>
                <button onClick={() => setEditing({ ...empty, order: items.length + 1 })} className="btn-copper !py-2 !px-4 text-sm" data-testid="ba-add">
                    <Plus className="w-4 h-4" /> Novo
                </button>
            </div>
            <p className="text-sm text-stone-500 mb-6">
                Estes exemplos aparecem na Home e na página de Remodelações. Quando existirem entradas ativas, substituem as fotos de demonstração.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((it) => (
                    <div key={it.id} className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid={`ba-item-${it.id}`}>
                        <div className="grid grid-cols-2">
                            <div className="relative aspect-square bg-stone-100">
                                {it.before_id && <img src={fileUrl(it.before_id)} alt="Antes" className="w-full h-full object-cover" />}
                                <span className="absolute top-2 left-2 text-[10px] font-mono uppercase tracking-wider bg-stone-950/80 text-white px-2 py-0.5 rounded-full">Antes</span>
                            </div>
                            <div className="relative aspect-square bg-stone-100">
                                {it.after_id && <img src={fileUrl(it.after_id)} alt="Depois" className="w-full h-full object-cover" />}
                                <span className="absolute top-2 right-2 text-[10px] font-mono uppercase tracking-wider bg-[hsl(var(--forest))] text-white px-2 py-0.5 rounded-full">Depois</span>
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="flex items-center gap-2 mb-1">
                                <GripVertical className="w-3 h-3 text-stone-400" />
                                <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">ordem {it.order}</span>
                                {!it.active && <span className="text-[10px] uppercase bg-stone-200 rounded-full px-2 py-0.5">Rascunho</span>}
                            </div>
                            <h3 className="font-display font-semibold">{it.title}</h3>
                            {it.subtitle && <p className="text-xs text-stone-500 mb-3">{it.subtitle}</p>}
                            <div className="flex gap-2 mt-2">
                                <button onClick={() => setEditing({ ...empty, ...it })} className="text-xs px-3 py-1 border border-stone-300 rounded" data-testid={`ba-edit-${it.id}`}>Editar</button>
                                <button onClick={() => del(it.id)} className="text-xs px-3 py-1 border border-red-200 text-red-600 rounded" data-testid={`ba-del-${it.id}`}><Trash2 className="w-3 h-3" /></button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <p className="text-stone-500 col-span-full text-center py-10">Ainda sem itens. Carregue "Novo" para adicionar o primeiro par.</p>}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-stone-950/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
                    <div className="bg-white rounded-xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 space-y-4">
                            <h2 className="font-display font-bold text-xl">{editing.id ? "Editar" : "Novo"} antes/depois</h2>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block text-sm">
                                    <span className="text-xs uppercase tracking-wider text-stone-500">Título *</span>
                                    <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full mt-1 px-3 py-2 border border-stone-300 rounded" data-testid="ba-title" />
                                </label>
                                <label className="block text-sm">
                                    <span className="text-xs uppercase tracking-wider text-stone-500">Subtítulo (localização)</span>
                                    <input value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} className="w-full mt-1 px-3 py-2 border border-stone-300 rounded" placeholder="Ex.: Apartamento T2 · Oeiras" />
                                </label>
                                <label className="block text-sm">
                                    <span className="text-xs uppercase tracking-wider text-stone-500">Ordem</span>
                                    <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 0 })} className="w-full mt-1 px-3 py-2 border border-stone-300 rounded" />
                                </label>
                                <label className="flex items-end gap-2 text-sm pb-2">
                                    <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-emerald-700" />
                                    <span>Ativo (visível no site)</span>
                                </label>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <ImageSlot label="Fotografia ANTES *" id={editing.before_id} loading={uploading === "before_id"}
                                    onFile={(f) => upload(f, "before_id")}
                                    onClear={() => setEditing((s) => ({ ...s, before_id: "" }))}
                                    testid="ba-before" />
                                <ImageSlot label="Fotografia DEPOIS *" id={editing.after_id} loading={uploading === "after_id"}
                                    onFile={(f) => upload(f, "after_id")}
                                    onClear={() => setEditing((s) => ({ ...s, after_id: "" }))}
                                    testid="ba-after" />
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button onClick={save} className="btn-copper flex-1 justify-center" data-testid="ba-save">Guardar</button>
                                <button onClick={() => setEditing(null)} className="px-6 py-2 border border-stone-300 rounded-full">Cancelar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ImageSlot({ label, id, onFile, onClear, loading, testid }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 block mb-2">{label}</label>
            {id ? (
                <div className="relative">
                    <img src={fileUrl(id)} alt="" className="w-full aspect-square object-cover rounded-lg" />
                    <button type="button" onClick={onClear} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">×</button>
                </div>
            ) : (
                <label className="flex flex-col items-center justify-center aspect-square border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-[hsl(var(--copper))] transition bg-stone-50" data-testid={testid}>
                    {loading ? (
                        <span className="text-xs text-stone-500">A carregar…</span>
                    ) : (
                        <>
                            <Upload className="w-5 h-5 text-stone-400 mb-2" />
                            <span className="text-xs text-stone-500">Carregar fotografia</span>
                        </>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
                </label>
            )}
        </div>
    );
}
